import { DatePipe, CommonModule, AsyncPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { LoadingSpinnerComponent } from 'src/app/core/loading-spinner/loading-spinner.component';
import { EnumToArrayPipe } from 'src/app/shared/pipes/enum-to-array.pipe';
import { TranslateAppInvitationStatusEnum } from 'src/app/shared/pipes/translate-app-invitation-status-enum.pipe';
import { BehaviorSubject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { AppInvitationStatus } from 'src/app/shared/enums/app-invitation-status.enum';
import { PagingOptions } from 'src/app/shared/models/paging';
import { AppInvitationsSearchEvent, ChargingUserAppInvitation } from '../charging-user.model';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'adm-app-invitation-list',
  templateUrl: './app-invitation-list-dialog.component.html',
  styleUrl: './app-invitation-list-dialog.component.scss',
  imports: [
    CommonModule,
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatMenuModule,
    LoadingSpinnerComponent,
    DatePipe,
    EnumToArrayPipe,
    TranslateAppInvitationStatusEnum,
    CustomDropDownArrowDirective,
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatMenuModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
})
export class AppInvitationListComponent extends PagerBase implements OnInit {
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<AppInvitationListComponent>>(MatDialogRef);
  private chargingUserService = inject(ChargingUserService);
  private snackbarService = inject(SnackbarService);

  public addressFoundAndSelected = false;
  public appInvitationsFilterModel: FormGroup<AppInvitationsFilterModel>;
  public appInvitationsSearchEvent: AppInvitationsSearchEvent;
  public appInvitations: ChargingUserAppInvitation[] = [];
  public statuses = AppInvitationStatus;
  public pageEvent = new PageEvent();
  public appInvitationsCount: number;
  public currentPage: number;
  public pagingOptions: PagingOptions;
  public loading$ = new BehaviorSubject<boolean>(false);
  public sending$ = new BehaviorSubject<boolean>(false);
  data$: BehaviorSubject<ChargingUserAppInvitation[]>;

  dataSource: MatTableDataSource<ChargingUserAppInvitation> = new MatTableDataSource<ChargingUserAppInvitation>();
  public displayColumns = [
    'chargingUsersName',
    'phoneNumber',
    'organizationName',
    'appInvitationStatus',
    'deepLinkCreated',
    'smsSent',
    'edit',
  ];

  ngOnInit() {
    this.pageEvent.pageSize = this.defaultPageSize;
    this.appInvitationsSearchEvent = new AppInvitationsSearchEvent(this.pageEvent);

    this.appInvitationsFilterModel = new FormGroup<AppInvitationsFilterModel>({
      query: new FormControl<string | null>(null),
      selectedAppInvitationStatus: new FormControl<AppInvitationStatus | null>(null),
    });

    this.appInvitationsFilterModel.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(async val => {
      this.loading$.next(true);

      this.appInvitationsSearchEvent.query = val.query;
      if (val.selectedAppInvitationStatus) {
        this.appInvitationsSearchEvent.appInvitationStatus = val.selectedAppInvitationStatus;
      } else {
        this.appInvitationsSearchEvent.appInvitationStatus = undefined;
      }
      this.loading$.next(true);
      this.getAppInvitations();
    });
    this.getAppInvitations();
    this.initPaginator();
  }

  public gotoSend(chargingUserAppInvitation: ChargingUserAppInvitation) {
    this.appInvitationsSearchEvent.chargingUserIdentifier = chargingUserAppInvitation.chargingUserIdentifier;
    this.dialogRef.close(chargingUserAppInvitation.chargingUserId);
  }

  public consoleLogDeepLink(chargingUserAppInvitation: ChargingUserAppInvitation) {
    console.warn(chargingUserAppInvitation.deepLink);
  }

  public sortData(sortEvent: Sort) {
    if (this.appInvitationsSearchEvent.paging) {
      this.appInvitationsSearchEvent.paging.pageIndex = 0;
    }
    if (sortEvent.direction === '') {
      sortEvent.direction = 'asc';
    }

    this.appInvitationsSearchEvent.sorting = sortEvent;
    this.getAppInvitations();
  }

  private initPaginator(): void {
    this.currentPage = 0;
    this.pagingOptions = {
      pageSize: this.pageEvent.pageSize,
      pageSizeOptions: this.pageSizes,
    };
  }

  public onPageChanged(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pagingOptions.pageSize = event.pageSize;
    this.appInvitationsSearchEvent.paging.pageIndex = event.pageIndex;
    this.getAppInvitations();
  }

  public close() {
    this.dialogRef.close();
  }

  public clearSearch() {
    this.appInvitationsFilterModel.get('selectedAppInvitationStatus')?.setValue(null);
  }

  getAppInvitations() {
    this.loading$.next(true);
    this.chargingUserService.getAppInvitations(this.appInvitationsSearchEvent).subscribe({
      next: result => {
        this.dataSource.data = result.items;
        this.appInvitationsCount = result.totalCount;
        this.currentPage = result.pageIndex;
        this.data$ = this.dataSource.connect();
        this.appInvitations = result.items;
        this.loading$.next(false);
      },
      error: () => {
        this.snackbarService.error('Fejl ved hentning af invitationer');
        this.loading$.next(false);
      },
    });
  }
}
interface AppInvitationsFilterModel {
  query: FormControl<string | null>;
  selectedAppInvitationStatus: FormControl<AppInvitationStatus | null>;
}
